/**
 * PostHog product-analytics integration.
 *
 * Autocapture records every click, page view, and form submission
 * without per-event instrumentation. Session replay records a video
 * of the user's actual session (with sensitive fields masked).
 *
 * Activates only when VITE_POSTHOG_KEY is set — in local dev with no
 * key, every exported function is a no-op so the app behaves
 * identically to pre-integration.
 *
 * Privacy:
 *  - UK/EU visitors (by device time zone) start WITHOUT cookie/localStorage
 *    identifiers, autocapture or session replay until they answer the
 *    consent banner; Decline switches capturing off (lib/analyticsConsent.ts,
 *    components/AnalyticsConsentBanner.tsx, 2026-10-08).
 *  - All <input>, <textarea>, and <select> contents are masked by
 *    default (PostHog's maskAllInputs).
 *  - Elements with `class="ph-no-capture"` are masked in both
 *    autocapture and session replay — use this for journal entries,
 *    DM bodies, mood notes, and the progress photo library.
 *  - On a real sign-out we call posthog.reset() so the next session
 *    doesn't inherit the previous user's identity (lib/auth signOut does
 *    it synchronously; useAuth also resets once the session settles to
 *    none on a device still identified as someone). Never on a cold start
 *    of a never-identified visitor — that wiped the ad visitor's anonymous
 *    id (posthogIdentityAction).
 *
 * Relationship to the existing analytics_events table:
 *  - PostHog covers the "everything" layer (automatic clicks, page
 *    views, session recordings, funnels).
 *  - Our backend analytics_events table stays for the deliberate,
 *    handcrafted events that need server-side correlation (onboarding
 *    completion, subscription transitions, etc.). Both run in parallel.
 */

// posthog-js is loaded LAZILY (2026-10-08). This module is imported
// statically by first-screen code (auth.ts, bootTiming, Gateway, Landing,
// RegisterPrompt, ProfileGate), so a static `import posthog from
// "posthog-js"` put ~185 KB of minified SDK into the main chunk that every
// ad visitor downloads before the first frame. Only the TYPE is imported
// statically now; initPostHog() fetches the SDK as its own chunk, and
// calls made before it arrives are queued and replayed in order.
import type { PostHog } from "posthog-js";
import { setSessionUrlProvider } from "@/lib/sentry";
import { optionalImport } from "@/lib/optionalChunk";
import { safeUrl, scrubUrlProps } from "@/lib/safeUrl";
import { currentAnalyticsMode, writeConsent, type AnalyticsMode } from "@/lib/analyticsConsent";
import { denyMetaPixel, grantMetaPixel, trackMetaFor } from "@/lib/metaPixel";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  "https://us.i.posthog.com";

let started = false;
/** The consent mode PostHog was started in, or last switched to. */
let mode: AnalyticsMode = "full";
/** The SDK once it has loaded and `init()` has been called on it. */
let posthog: PostHog | null = null;
/** Calls made before the SDK arrived. Capped so a blocked SDK (ad blocker,
 *  offline first visit) can't grow it forever — the oldest are kept, since
 *  they carry the landing/attribution events. */
const pending: Array<(ph: PostHog) => void> = [];
const MAX_PENDING = 200;

/** Run `fn` against PostHog now if it's loaded, else once it loads. */
function withPostHog(fn: (ph: PostHog) => void): void {
  if (!POSTHOG_KEY) return;
  if (posthog) {
    fn(posthog);
    return;
  }
  if (pending.length < MAX_PENDING) pending.push(fn);
}

/** Initialise PostHog once at app boot. Safe to call even when the
 *  key isn't set — becomes a no-op in that case. */
export function initPostHog(): void {
  if (started) return;
  if (!POSTHOG_KEY) {
    // No key → no-op. Matches dev / preview environments where we
    // don't want to pollute the real PostHog project with test data.
    return;
  }
  started = true;
  // Don't start while the address still holds sign-in keys or a hand-off
  // problem (/#access_token=…, #pilot_problem=…): the SDK stores the FIRST
  // address it sees as $initial_person_info and re-sends it on every /flags
  // call, which before_send never sees (review 2026-10-08). The auth client
  // tidies the address within a moment; calls made meanwhile are queued.
  waitForCleanAddress()
    .then(() => optionalImport(() => import("posthog-js")))
    .then(({ default: sdk }) => {
      startPostHog(sdk);
      posthog = sdk;
      for (const fn of pending.splice(0)) {
        try { fn(sdk); } catch { /* one bad call must not drop the rest */ }
      }
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("posthog load failed:", err);
    });
}

const SENSITIVE_HASH = /(^#|&)(access_token|refresh_token|provider_token|pilot_problem)=/;

/** Resolves once the address bar no longer carries keys or a hand-off
 *  problem — or after 10 s, when before_send and the persisted-value clean
 *  below still keep them out. */
export function waitForCleanAddress(timeoutMs = 10_000): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !SENSITIVE_HASH.test(window.location.hash)) { resolve(); return; }
    const started = Date.now();
    const timer = window.setInterval(() => {
      if (!SENSITIVE_HASH.test(window.location.hash) || Date.now() - started > timeoutMs) {
        window.clearInterval(timer);
        resolve();
      }
    }, 150);
  });
}

/** Devices that stored a key-bearing first address before 1.3.170 keep
 *  re-sending it on /flags; clean the stored copy (best effort — the
 *  persistence API is internal). */
function cleanStoredAddress(sdk: PostHog): void {
  try {
    const store = (sdk as unknown as { persistence?: { props?: Record<string, unknown>; save?: () => void } }).persistence;
    const info = store?.props?.$initial_person_info as { u?: string } | undefined;
    if (info?.u && info.u !== safeUrl(info.u)) {
      info.u = safeUrl(info.u);
      store?.save?.();
    }
  } catch { /* best effort */ }
}

function startPostHog(sdk: PostHog): void {
  if (!POSTHOG_KEY) return;
  // Read when the SDK arrives, not at boot: a banner choice made while it was
  // still downloading starts it in the right mode straight away.
  mode = currentAnalyticsMode();
  const full = mode === "full";
  const bootstrapId = bootstrapDistinctId({
    handoffId,
    mode,
    // Memory persistence never reads what's stored, so only "full" can have
    // an id of its own to keep.
    hasStoredId: full && hasStoredPostHogId(POSTHOG_KEY),
  });
  try {
    sdk.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only", // create profile rows only for logged-in users
      // UK/EU before a choice, or after Decline: an in-memory id only (no
      // cookie or localStorage identifier), no autocapture, no replay.
      persistence: full ? "localStorage+cookie" : "memory",
      autocapture: full,
      disable_session_recording: !full,
      // Heatmaps and dead clicks listen to every click and mouse move on
      // their own, switched on from the PostHog project settings unless the
      // code says otherwise — so before a choice they'd still track clicks
      // (review 2026-10-09). Same switch as autocapture.
      capture_heatmaps: full,
      capture_dead_clicks: full,
      // Declined: nothing is captured, not even the first page view.
      ...(mode === "denied" ? { opt_out_capturing_by_default: true } : {}),
      // Arrived from another browser (Facebook's in-app browser → Safari, or
      // the confirmation email opened elsewhere) carrying that browser's id:
      // carry on as the same anonymous person, so identify() later joins the
      // ad visit to the signup.
      ...(bootstrapId ? { bootstrap: { distinctID: bootstrapId } } : {}),
      // One automatic $pageview, for the page the visitor LANDS on (history
      // changes aren't captured — that needs "history_change"). In-app route
      // changes are sent as `page_view` by usePageTracking (App.tsx), and the
      // ad funnel's first step is `gateway.viewed`, not this $pageview.
      capture_pageview: true,
      // Sign-in lands on /#access_token=…&refresh_token=…, and the first page
      // view fired on that address — live sign-in keys reached PostHog
      // (2026-10-08). Every event's URL properties are cleaned here.
      before_send: (event) => {
        if (!event) return event;
        cleanStoredAddress(sdk);
        scrubUrlProps(event.properties);
        scrubUrlProps(event.$set);
        scrubUrlProps(event.$set_once);
        return event;
      },
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,
        // CSS selector for extra elements to mask in session replays.
        // Sensitive areas (journal body, DM thread, mood notes, photo
        // library) should carry `class="ph-no-capture"` at the
        // container level.
        maskTextSelector: ".ph-no-capture, .ph-mask, [data-ph-mask='true']",
        // Replays record the page address too (meta / $pageview hrefs go
        // through this same hook) — clean it like every other URL.
        maskCapturedNetworkRequestFn: (r) => ({ ...r, name: safeUrl(r.name) }),
      },
      loaded: () => {
        // Our stored choice is the source of truth; PostHog's own opt-out
        // flag (the SDK keeps it in localStorage) mirrors it, so a choice
        // cleared from Privacy → Cookie settings can't leave a stale opt-out.
        try {
          if (mode === "denied") sdk.opt_out_capturing();
          else if (sdk.has_opted_out_capturing()) sdk.opt_in_capturing({ captureEventName: null });
        } catch { /* best effort */ }
        // The running JS bundle version on every event. A Capgo alert on
        // 2026-09-09 came from a phone still on 1.3.11 (three weeks old) that
        // had failed every download since — and nothing told us how many
        // phones were like it. PostHog can now answer "who is stuck on which
        // version" (distinct persons by app_version, last 7 days).
        registerAppVersion(sdk);
        cleanStoredAddress(sdk);
        // ── PostHog ↔ Sentry link ─────────────────────────────────────────
        // Register a provider so lib/sentry.ts's `beforeSend` hook can
        // attach the current PostHog session replay URL to every Sentry
        // event. This makes a Sentry issue link to its PostHog replay and
        // vice-versa — no circular import (sentry.ts never imports posthog).
        setSessionUrlProvider(() => {
          try {
            return (
              sdk.get_session_replay_url?.({ withTimestamp: true }) ?? null
            );
          } catch {
            return null;
          }
        });
      },
    });
  } catch (err) {
    // Never throw — analytics failure must not break the app boot.
    // eslint-disable-next-line no-console
    console.warn("posthog init failed:", err);
  }
}

/** `register()` writes a super property into PostHog persistence, and
 *  `posthog.reset()` (a real sign-out, or a different person signing in on
 *  this device) wipes persistence — so a one-off register in `loaded` left
 *  events without the version (review 2026-09-09). Re-registered after every
 *  reset and identify.
 *
 *  (Until 2026-10-07 reset also ran on EVERY cold start, before the session
 *  resolved — which wiped the anonymous id between the ad visit, the email
 *  confirmation reload and the Google redirect, so ad → signup → Pilot start
 *  could never be joined. See posthogIdentityAction.) */
function registerAppVersion(sdk: PostHog): void {
  try {
    sdk.register({ app_version: typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "unknown" });
  } catch { /* best-effort */ }
}

// The user id this device last identified PostHog as. Survives cold starts so
// "a different person signs in" is detected even when the previous person's
// session simply expired instead of signing out.
const IDENTIFIED_KEY = "trybeup_ph_identified";

export function readIdentifiedId(): string | null {
  try { return localStorage.getItem(IDENTIFIED_KEY); } catch { return null; }
}

export type PostHogIdentityAction = "identify" | "reset_and_identify" | "reset" | "none";

/**
 * What to do with PostHog's identity on an auth-state change. Pure, so it's
 * testable without PostHog.
 *
 * - signed in → identify; if this device was identified as SOMEONE ELSE,
 *   reset first so the two people's events never merge.
 * - session went from present to absent (a real sign-out) → reset.
 * - the session has SETTLED to none but this device is still identified as
 *   someone (their session expired, or a sign-out navigated away before the
 *   reset ran) → reset, so the next visitor isn't recorded as them.
 * - no session and this device was never identified (cold start of an ad
 *   visitor, the email confirmation reload, the Google redirect), or the
 *   session hasn't settled yet → NOTHING. Resetting here wiped the anonymous
 *   id the ad visit was recorded under, so the visit and the signup that
 *   followed it looked like two different people.
 */
export function posthogIdentityAction(opts: {
  hadSession: boolean;
  hasSession: boolean;
  /** False while the initial session is still being restored. Defaults to
   *  false, so a caller that doesn't know never wipes an identity early. */
  sessionSettled?: boolean;
  userId: string | null | undefined;
  identifiedId: string | null | undefined;
}): PostHogIdentityAction {
  const { hadSession, hasSession, sessionSettled = false, userId, identifiedId } = opts;
  if (hasSession) {
    if (!userId) return "none"; // session resolved, user row still loading
    return identifiedId && identifiedId !== userId ? "reset_and_identify" : "identify";
  }
  if (hadSession) return "reset";
  return sessionSettled && identifiedId ? "reset" : "none";
}

/** Tie the current browser session to a known user. Call on login. */
export function identifyUser(
  userId: string,
  traits: Record<string, unknown> = {},
): void {
  // The device key is written only once PostHog has really identified, so
  // it always mirrors PostHog's own persisted identity.
  withPostHog((sdk) => {
    try {
      sdk.identify(userId, traits);
      // Not in memory mode (UK/EU before a choice, or declined): that
      // identity ends with the page, so there's nothing for the key to mirror
      // — and it would be an analytics identifier stored without consent.
      if (mode === "full") {
        try { localStorage.setItem(IDENTIFIED_KEY, userId); } catch { /* ignore */ }
      }
      registerAppVersion(sdk);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("posthog identify failed:", err);
    }
  });
}

/** Clear the user association. Call on a real sign-out (or before a
 *  different user is identified) so the next user's actions don't get
 *  attributed to the previous one — never on a plain cold start. */
export function resetPostHog(): void {
  // Synchronous once PostHog has loaded (always the case by the time anyone
  // can sign out). If it hasn't, the reset is queued and the device key is
  // KEPT until it runs — so if the page navigates away first, the next boot
  // still sees "identified, no session" and posthogIdentityAction resets then.
  withPostHog((sdk) => {
    try {
      sdk.reset();
      try { localStorage.removeItem(IDENTIFIED_KEY); } catch { /* ignore */ }
      registerAppVersion(sdk);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("posthog reset failed:", err);
    }
  });
}

/** Fire a custom event. Most of the time autocapture covers you;
 *  this is for deliberate events like 'subscription_upgraded' or
 *  'onboarding_step_complete' where you want a named event in PostHog
 *  for funnels. Existing analytics_events tracking stays in parallel. */
export function capturePostHog(event: string, props: Record<string, unknown> = {}): void {
  // The few funnel steps the Meta Pixel measures ride on these same events
  // (lib/metaPixel.ts decides which; it runs even without a PostHog key).
  trackMetaFor(event, props);
  // Stamp WHEN and WHERE it happened now: a call queued before the SDK loads
  // is replayed later, and would otherwise carry the replay's time and URL —
  // landing events then sorted after the initial $pageview and broke
  // $pageview-first funnels (review of 1.3.160).
  const queuedAt = posthog ? null : new Date();
  const currentUrl = typeof window !== "undefined" ? safeUrl(window.location.href) : undefined;
  withPostHog((sdk) => {
    try {
      if (queuedAt) {
        sdk.capture(event, { ...props, ...(currentUrl ? { $current_url: currentUrl } : {}) }, { timestamp: queuedAt });
      } else {
        sdk.capture(event, props);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("posthog capture failed:", err);
    }
  });
}

// ── Consent (UK/EU) ──────────────────────────────────────────────────────
// Called by the consent banner; the same choice covers the Meta Pixel
// (lib/metaPixel.ts). Both store the choice first, so it holds even
// if PostHog never loads (ad blocker) or the page closes before it does; the
// SDK change is queued until then.

/** Accept: switch the running SDK to normal analytics in place.
 *
 *  Verified against posthog-js 1.372.1 (lib/src/posthog-core.js set_config →
 *  posthog-persistence.js update_config): changing `persistence` migrates the
 *  in-memory id and properties into localStorage+cookie, and set_config
 *  re-runs autocapture.startIfEnabled() and
 *  sessionRecording.startIfEnabledOrStop(), so nothing waits for a reload. */
export function grantAnalyticsConsent(): void {
  writeConsent("granted");
  grantMetaPixel();
  withPostHog((sdk) => {
    try {
      mode = "full";
      if (sdk.has_opted_out_capturing()) sdk.opt_in_capturing({ captureEventName: null });
      sdk.set_config({ persistence: "localStorage+cookie", autocapture: true, capture_heatmaps: true, capture_dead_clicks: true });
      sdk.startSessionRecording?.();
      // identifyUser skips the device key in memory mode; now that the
      // identity is stored, mirror it like a normal sign-in would.
      if (sdk.get_property("$user_state") === "identified") {
        try { localStorage.setItem(IDENTIFIED_KEY, sdk.get_distinct_id()); } catch { /* ignore */ }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("posthog consent upgrade failed:", err);
    }
  });
}

/** Decline: stop capturing and keep (or go back to) the in-memory id. The
 *  rest of the app is unaffected. */
export function denyAnalyticsConsent(): void {
  writeConsent("denied");
  denyMetaPixel();
  withPostHog((sdk) => {
    try {
      const wasFull = mode === "full";
      mode = "denied";
      sdk.opt_out_capturing();
      if (wasFull) {
        // Only after an earlier Accept (or a non-EU visitor choosing from
        // Cookie settings): moving to memory clears PostHog's cookie and
        // localStorage copy, and replay stops.
        sdk.stopSessionRecording?.();
        // Memory persistence clears the cookie + localStorage copy but not
        // PostHog's separate sessionStorage one (review 2026-10-09).
        try { sessionStorage.removeItem(`ph_${POSTHOG_KEY}_posthog`); } catch { /* ignore */ }
        sdk.set_config({ persistence: "memory", autocapture: false, capture_heatmaps: false, capture_dead_clicks: false });
        try { localStorage.removeItem(IDENTIFIED_KEY); } catch { /* ignore */ }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("posthog opt-out failed:", err);
    }
  });
}

// ── One person across browsers (2026-10-08) ──────────────────────────────
// A Facebook/Instagram ad opens in the app's own webview; Google sign-in
// can't work there, so the visitor copies a hand-off link into Safari — a
// browser with none of the webview's storage, so PostHog saw a NEW person.
// Same for the confirmation email opened in another browser or app. Both
// links now carry this browser's PostHog id as `ph_did`, and the receiving
// browser starts as that id (bootstrap) when it has none of its own.

export const HANDOFF_ID_PARAM = "ph_did";
/** PostHog ids are UUIDs (v7 for anonymous ones); allow a little slack but
 *  nothing that could carry free text. */
const HANDOFF_ID_FORMAT = /^[A-Za-z0-9-]{8,64}$/;

/** Pure: the `ph_did` value if it looks like a PostHog id, else null. */
export function validHandoffId(value: string | null | undefined): string | null {
  return value && HANDOFF_ID_FORMAT.test(value) ? value : null;
}

/** Pure: whether to start PostHog as the hand-off id. Never over an id this
 *  browser already has (the SDK's bootstrap would silently replace it), and
 *  only with full consent: joining two browsers' visits is exactly what a
 *  UK/EU visitor who hasn't chosen yet hasn't agreed to (review 2026-10-09). */
export function bootstrapDistinctId(opts: {
  handoffId: string | null | undefined;
  hasStoredId: boolean;
  mode: AnalyticsMode;
}): string | null {
  const id = validHandoffId(opts.handoffId);
  if (!id || opts.mode !== "full" || opts.hasStoredId) return null;
  return id;
}

/** Pure: `url` with `ph_did` added (unchanged when there's no valid id). */
export function withHandoffId(url: string, id: string | null | undefined): string {
  const valid = validHandoffId(id);
  if (!valid) return url;
  try {
    const u = new URL(url);
    u.searchParams.set(HANDOFF_ID_PARAM, valid);
    return u.toString();
  } catch {
    return url;
  }
}

/** Does this browser already hold a PostHog id (localStorage or cookie)? */
function hasStoredPostHogId(token: string): boolean {
  const name = `ph_${token}_posthog`;
  try {
    const raw = localStorage.getItem(name);
    if (raw && (JSON.parse(raw) as { distinct_id?: unknown })?.distinct_id) return true;
  } catch { /* unreadable → fall through to the cookie */ }
  try {
    return typeof document !== "undefined" && document.cookie.split("; ").some((c) => c.startsWith(`${name}=`));
  } catch {
    return false;
  }
}

/** Read `ph_did` from the address and take it out of the address bar. Runs
 *  when this module is first evaluated — it's imported statically by the
 *  first-screen code (auth.ts), so that's before React Router reads the
 *  address. The rest of the query and the #fragment (sign-in keys the auth
 *  client still has to read) are left exactly as they were. */
function takeHandoffIdFromAddress(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(HANDOFF_ID_PARAM)) return null;
    const id = validHandoffId(url.searchParams.get(HANDOFF_ID_PARAM));
    url.searchParams.delete(HANDOFF_ID_PARAM);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    return id;
  } catch {
    return null;
  }
}

const handoffId = takeHandoffIdFromAddress();

/** This browser's PostHog id, for a hand-off link — null until the SDK has
 *  loaded, without full consent (UK/EU before a choice, or after Decline), or
 *  once signed in (a signed-in id is the account's
 *  own and mustn't be handed to whoever opens the link). */
export function getPostHogDistinctId(): string | null {
  if (!posthog || mode !== "full") return null;
  try {
    if (posthog.has_opted_out_capturing()) return null;
    if (posthog.get_property("$user_state") === "identified") return null;
    return validHandoffId(posthog.get_distinct_id());
  } catch {
    return null;
  }
}
